import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, BookOpen, BookMarked, FolderOpen, FileText, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useArticles, KBArticle } from '@/hooks/useKnowledgeBase';
import { useUserRole } from '@/hooks/useUserRole';

interface KBSidebarProps {
  currentSlug?: string;
  onArticleSelect?: (slug: string) => void;
}

const CATEGORY_CONFIG: Record<string, { label: string; icon: React.ElementType; order: number }> = {
  playbook: { label: 'Playbooks', icon: BookOpen, order: 1 },
  guide: { label: 'Guias', icon: BookMarked, order: 2 },
  reference: { label: 'Referência', icon: FolderOpen, order: 3 },
};

export function KBSidebar({ currentSlug, onArticleSelect }: KBSidebarProps) {
  const navigate = useNavigate();
  const { data: articles = [], isLoading } = useArticles();
  const { role } = useUserRole();
  const isAdmin = role === 'admin';
  
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(['playbook', 'guide', 'reference'])
  );
  const [expandedPlaybooks, setExpandedPlaybooks] = useState<Set<string>>(new Set());

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const togglePlaybook = (playbookId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newExpanded = new Set(expandedPlaybooks);
    if (newExpanded.has(playbookId)) {
      newExpanded.delete(playbookId);
    } else {
      newExpanded.add(playbookId);
    }
    setExpandedPlaybooks(newExpanded);
  };

  const handleArticleClick = (slug: string) => {
    if (onArticleSelect) {
      onArticleSelect(slug);
    } else {
      navigate(`/knowledge-base/${slug}`);
    }
  };

  // Organize articles into hierarchy
  const { playbooks, guides, references, childrenByParent } = useMemo(() => {
    const playbooks: KBArticle[] = [];
    const guides: KBArticle[] = [];
    const references: KBArticle[] = [];
    const childrenByParent: Record<string, KBArticle[]> = {};

    articles.forEach((article) => {
      if (article.parent_id) {
        // This is a child article
        if (!childrenByParent[article.parent_id]) {
          childrenByParent[article.parent_id] = [];
        }
        childrenByParent[article.parent_id].push(article);
      } else {
        // This is a root article
        switch (article.category) {
          case 'playbook':
            playbooks.push(article);
            break;
          case 'guide':
            guides.push(article);
            break;
          case 'reference':
            references.push(article);
            break;
          default:
            guides.push(article);
        }
      }
    });

    // Sort children by sort_order
    Object.keys(childrenByParent).forEach((parentId) => {
      childrenByParent[parentId].sort((a, b) => a.sort_order - b.sort_order);
    });

    return { playbooks, guides, references, childrenByParent };
  }, [articles]);

  // Auto-expand playbook containing current article
  useMemo(() => {
    if (currentSlug) {
      const currentArticle = articles.find((a) => a.slug === currentSlug);
      if (currentArticle?.parent_id) {
        setExpandedPlaybooks((prev) => new Set([...prev, currentArticle.parent_id!]));
      }
    }
  }, [currentSlug, articles]);

  if (isLoading) {
    return (
      <div className="w-64 border-r border-border bg-muted/30 p-4">
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-8 bg-muted rounded" />
          ))}
        </div>
      </div>
    );
  }

  const renderArticleItem = (article: KBArticle, isChild: boolean = false) => {
    const children = childrenByParent[article.id] || [];
    const hasChildren = children.length > 0;
    const isPlaybook = article.category === 'playbook' && !article.parent_id;
    const isExpanded = expandedPlaybooks.has(article.id);

    return (
      <div key={article.id}>
        <div
          className={cn(
            "w-full flex items-center gap-1 text-sm rounded-md transition-colors",
            isChild ? "pl-6" : "",
            currentSlug === article.slug
              ? "bg-primary/10 text-primary font-medium"
              : "text-muted-foreground hover:text-foreground hover:bg-muted"
          )}
        >
          {isPlaybook && hasChildren && (
            <button
              onClick={(e) => togglePlaybook(article.id, e)}
              className="p-1.5 hover:bg-muted rounded"
            >
              {isExpanded ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
            </button>
          )}
          <button
            onClick={() => handleArticleClick(article.slug)}
            className={cn(
              "flex-1 text-left px-2 py-1.5 truncate",
              !isPlaybook || !hasChildren ? "pl-3" : ""
            )}
            title={article.title}
          >
            {article.title}
            {isPlaybook && hasChildren && (
              <span className="ml-1 text-xs text-muted-foreground">
                ({children.length})
              </span>
            )}
          </button>
        </div>

        {/* Render children if expanded */}
        {isPlaybook && hasChildren && isExpanded && (
          <div className="ml-2 mt-0.5 space-y-0.5 border-l border-border pl-2">
            {children.map((child) => renderArticleItem(child, true))}
          </div>
        )}
      </div>
    );
  };

  const renderCategory = (
    category: string,
    categoryArticles: KBArticle[],
    config: { label: string; icon: React.ElementType }
  ) => {
    if (categoryArticles.length === 0) return null;

    const Icon = config.icon;
    const isExpanded = expandedCategories.has(category);

    return (
      <div key={category}>
        <button
          onClick={() => toggleCategory(category)}
          className="w-full flex items-center gap-2 px-2 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
        >
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
          <Icon className="h-4 w-4" />
          <span>{config.label}</span>
          <span className="ml-auto text-xs text-muted-foreground">
            {categoryArticles.length}
          </span>
        </button>

        {isExpanded && (
          <div className="ml-4 mt-1 space-y-0.5">
            {categoryArticles.map((article) => renderArticleItem(article))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-64 border-r border-border bg-muted/30 flex flex-col">
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-1">
          {renderCategory('playbook', playbooks, CATEGORY_CONFIG.playbook)}
          {renderCategory('guide', guides, CATEGORY_CONFIG.guide)}
          {renderCategory('reference', references, CATEGORY_CONFIG.reference)}
        </div>
      </ScrollArea>

      {isAdmin && (
        <div className="p-3 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => navigate('/knowledge-base/new')}
          >
            <Plus className="h-4 w-4 mr-2" />
            Novo Artigo
          </Button>
        </div>
      )}
    </div>
  );
}
